import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ListPlaceholderTemplatesResponse,
  GetPlaceholderTemplateResponse,
  DeletePlaceholderTemplateRequest,
  SyncPlaceholderTemplateRequest,
  SyncPlaceholderTemplateResponse,
  SyncAllPlaceholderTemplatesRequest,
  SyncAllPlaceholderTemplatesResponse,
  PlaceholderTemplateSummary,
  PlaceholderTemplateDetail,
  PlaceholderTemplateSourceMappingDetail,
  PlaceholderStatus,
  UpdateHomeIfcFileRequest,
  SituViewWebApiClient
} from 'situview-webapi-client';
import { AuthService, ConfirmDialogService, StateService } from 'src/app/services';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import {
  PlaceholderTemplateDialogComponent,
  PlaceholderTemplateDialogData
} from './placeholder-template-dialog/placeholder-template-dialog.component';

@Component({
  selector: 'app-placeholder-templates',
  templateUrl: './placeholder-templates.component.html',
  styleUrls: ['./placeholder-templates.component.less']
})
export class PlaceholderTemplatesComponent extends SituBaseComponent {
  loading = false;
  templates: PlaceholderTemplateSummary[] = [];
  searchTerm = '';
  expandedTemplateId: string | undefined;
  expandedDetail: PlaceholderTemplateDetail | undefined;
  expandedFiles: { name: string; source: string; isMaster: boolean }[] = [];
  loadingDetail = false;
  syncingTemplateId: string | undefined;
  syncingAll = false;
  // PORTAL-2203: id of the HomeIfcFile whose override is currently being toggled (disables its chip)
  overridingFileId: string | undefined;

  // Summary stats
  get totalTemplates(): number { return this.templates.length; }
  get totalAffectedHomes(): number {
    return this.templates.reduce(
      (sum, t) => sum + (t.affectedHomes?.length ?? 0), 0
    );
  }
  get totalSourceHomes(): number {
    return this.templates.reduce(
      (sum, t) => sum + (t.sourceCount ?? 0), 0
    );
  }
  get totalIfcFiles(): number {
    return this.templates.reduce(
      (sum, t) => sum + (t.totalIfcFiles ?? 0), 0
    );
  }

  placeholderStatus = PlaceholderStatus;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private dialogService: ConfirmDialogService,
    private situViewApiClient: SituViewWebApiClient
  ) {
    super(authService, stateService);
  }

  protected onSelectedCompanyChange(): void {
    this.loadData();
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  loadData(): void {
    this.loading = true;

    this.situViewApiClient.listPlaceholderTemplates(
      this.selectedCompany?.id,
      this.searchTerm || undefined
    ).then((response: ListPlaceholderTemplatesResponse) => {
        if (response.success) {
          this.templates = response.items ?? [];
        } else {
          this.showMessage('Failed to load placeholder templates');
        }
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
        this.showMessage('Could not retrieve placeholder templates');
      });
  }

  toggleExpand(template: PlaceholderTemplateSummary): void {
    if (this.expandedTemplateId === template.id) {
      this.expandedTemplateId = undefined;
      this.expandedDetail = undefined;
      this.expandedFiles = [];
      return;
    }

    this.expandedTemplateId = template.id;
    this.expandedDetail = undefined;
    this.loadingDetail = true;

    this.situViewApiClient.getPlaceholderTemplate(
      this.selectedCompany?.id, template.id
    ).then((response: GetPlaceholderTemplateResponse) => {
        if (response.success && response.template) {
          this.expandedDetail = response.template;
          this.expandedFiles = this.getAllFilesForTemplate(response.template);
        } else {
          this.showMessage('Failed to load template details');
        }
        this.loadingDetail = false;
      })
      .catch(() => {
        this.loadingDetail = false;
        this.showMessage('Could not load template details');
      });
  }

  openAddDialog(): void {
    const dialogData: PlaceholderTemplateDialogData = {
      isEdit: false,
      companyId: this.selectedCompany?.id
    };

    const dialogRef = this.dialog.open(
      PlaceholderTemplateDialogComponent,
      { width: '760px', data: dialogData, disableClose: true }
    );

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.loadData();
      }
    });
  }

  openEditDialog(template: PlaceholderTemplateSummary, event: Event): void {
    event.stopPropagation();

    // Load the full detail first
    this.situViewApiClient.getPlaceholderTemplate(
      this.selectedCompany?.id, template.id
    ).then((response: GetPlaceholderTemplateResponse) => {
        if (response.success && response.template) {
          const dialogData: PlaceholderTemplateDialogData = {
            isEdit: true,
            companyId: this.selectedCompany?.id,
            template: response.template
          };

          const dialogRef = this.dialog.open(
            PlaceholderTemplateDialogComponent,
            { width: '760px', data: dialogData, disableClose: true }
          );

          dialogRef.afterClosed().subscribe((result: boolean) => {
            if (result) {
              this.loadData();
              // Refresh expanded detail if this one is expanded
              if (this.expandedTemplateId === template.id) {
                this.expandedTemplateId = undefined;
                this.expandedDetail = undefined;
              }
            }
          });
        } else {
          this.showMessage('Failed to load template for editing');
        }
      })
      .catch(() => {
        this.showMessage('Could not load template for editing');
      });
  }

  syncTemplate(template: PlaceholderTemplateSummary, event: Event): void {
    event.stopPropagation();
    this.syncingTemplateId = template.id;

    const request = new SyncPlaceholderTemplateRequest();
    request.companyId = this.selectedCompany?.id;
    request.templateId = template.id;

    this.situViewApiClient.syncPlaceholderTemplate(request)
      .then((response: SyncPlaceholderTemplateResponse) => {
        if (response.success) {
          const copied = response.filesCopied ?? 0;
          const skipped = (response as any).filesSkipped ?? 0;
          let msg = `Synced ${copied} file${copied !== 1 ? 's' : ''}`;
          if (skipped > 0) {
            msg += `, ${skipped} skipped (originals exist)`;
          }
          this.showMessage(msg);
          this.loadData();
          // Refresh expanded detail if open
          if (this.expandedTemplateId === template.id) {
            this.expandedTemplateId = undefined;
            this.expandedDetail = undefined;
          }
        } else {
          this.showMessage('Sync failed');
        }
        this.syncingTemplateId = undefined;
      })
      .catch(() => {
        this.syncingTemplateId = undefined;
        this.showMessage('Could not sync template');
      });
  }

  syncAllTemplates(): void {
    if (this.syncingAll || this.templates.length === 0) return;

    const options = {
      title: 'Sync all templates?',
      message: `Sync all ${this.templates.length} templates in this company? This may take a while.`,
      cancelText: 'Cancel',
      confirmText: 'Sync All',
      actionIcon: 'sync',
      actionColour: 'primary'
    } as ConfirmDialogData;

    this.dialogService.open(options);
    this._subscriptions.push(this.dialogService.confirmed().subscribe(confirmed => {
      if (!confirmed) return;
      this.runSyncAll();
    }));
  }

  private runSyncAll(): void {
    this.syncingAll = true;

    const request = new SyncAllPlaceholderTemplatesRequest();
    request.companyId = this.selectedCompany?.id;

    this.situViewApiClient.syncAllPlaceholderTemplates(request)
      .then((response: SyncAllPlaceholderTemplatesResponse) => {
        if (response.success) {
          const processed = response.templatesProcessed ?? 0;
          const succeeded = response.templatesSucceeded ?? 0;
          const failed = response.templatesFailed ?? 0;
          const copied = response.totalFilesCopied ?? 0;
          const skipped = response.totalFilesSkipped ?? 0;

          let msg = `Synced ${succeeded}/${processed} templates ` +
            `\u2014 ${copied} file${copied !== 1 ? 's' : ''} copied`;
          if (skipped > 0) {
            msg += `, ${skipped} skipped`;
          }
          if (failed > 0) {
            const failedNames = (response.results ?? [])
              .filter(r => !r.success)
              .map(r => r.templateName)
              .filter(n => !!n)
              .slice(0, 3)
              .join(', ');
            msg += `. ${failed} failed`;
            if (failedNames) {
              msg += `: ${failedNames}`;
              if (failed > 3) msg += `, +${failed - 3} more`;
            }
          }
          this.snackBar.open(msg, 'OK', { duration: 6000 });
          this.loadData();
          if (this.expandedTemplateId) {
            this.expandedTemplateId = undefined;
            this.expandedDetail = undefined;
          }
        } else {
          this.showMessage('Sync all failed');
        }
        this.syncingAll = false;
      })
      .catch(() => {
        this.syncingAll = false;
        this.showMessage('Could not sync all templates');
      });
  }

  deleteTemplate(template: PlaceholderTemplateSummary, event: Event): void {
    event.stopPropagation();

    const options = {
      title: 'Delete template?',
      message: `Delete template "${template.name}"? This cannot be undone.`,
      cancelText: 'Cancel',
      confirmText: 'Delete',
      actionIcon: 'delete_outline',
      actionColour: 'warn'
    } as ConfirmDialogData;

    this.dialogService.open(options);
    this._subscriptions.push(this.dialogService.confirmed().subscribe(confirmed => {
      if (!confirmed) return;
      this.runDelete(template);
    }));
  }

  private runDelete(template: PlaceholderTemplateSummary): void {
    const request = new DeletePlaceholderTemplateRequest();
    request.companyId = this.selectedCompany?.id;
    request.templateId = template.id;

    this.situViewApiClient.deletePlaceholderTemplate(request)
      .then((response) => {
        if (response.success) {
          this.showMessage('Template deleted');
          this.loadData();
        } else {
          this.showMessage('Failed to delete template');
        }
      })
      .catch(() => {
        this.showMessage('Could not delete template');
      });
  }

  getStatusLabel(status: PlaceholderStatus | undefined): string {
    switch (status) {
      case PlaceholderStatus.Active: return 'Active';
      case PlaceholderStatus.SourceUpdated: return 'Source Updated';
      case PlaceholderStatus.PlaceholderUpdated: return 'Placeholder Updated';
      case PlaceholderStatus.Promoted: return 'Promoted';
      default: return 'None';
    }
  }

  getStatusClass(status: PlaceholderStatus | undefined): string {
    switch (status) {
      case PlaceholderStatus.Active: return 'status-active';
      case PlaceholderStatus.SourceUpdated: return 'status-source_updated';
      case PlaceholderStatus.PlaceholderUpdated: return 'status-placeholder_updated';
      case PlaceholderStatus.Promoted: return 'status-promoted';
      default: return '';
    }
  }

  getStatusIcon(status: PlaceholderStatus | undefined): string {
    switch (status) {
      case PlaceholderStatus.Active: return 'check_circle';
      case PlaceholderStatus.SourceUpdated: return 'sync';
      case PlaceholderStatus.PlaceholderUpdated: return 'info';
      case PlaceholderStatus.Promoted: return 'star';
      default: return 'help_outline';
    }
  }

  getAllFilesForTemplate(
    detail: PlaceholderTemplateDetail
  ): { name: string; source: string; isMaster: boolean }[] {
    const allFiles: { name: string; source: string; isMaster: boolean }[] = [];
    if (!detail.sourceMappings) return allFiles;
    detail.sourceMappings.forEach(
      (m: PlaceholderTemplateSourceMappingDetail) => {
        (m.sourceFiles ?? []).forEach(f => {
          allFiles.push({
            name: f.name ?? '',
            source: m.sourceHomeName ?? '',
            isMaster: f.type === 'Master'
          });
        });
      }
    );
    return allFiles;
  }

  hasAnyFileStatuses(): boolean {
    if (!this.expandedDetail?.affectedHomes) return false;
    return this.expandedDetail.affectedHomes.some(
      h => (h as any).fileStatuses?.length > 0
    );
  }

  getFileStatusIcon(status: string): string {
    switch (status) {
      case 'original': return 'insert_drive_file';
      case 'placeholder': return 'content_copy';
      case 'overridden': return 'published_with_changes';
      case 'missing': return 'remove_circle_outline';
      default: return 'help_outline';
    }
  }

  getFileStatusTooltip(fs: any): string {
    switch (fs.status) {
      case 'original': return `${fs.fileName}: Original file — sync will skip. Click to override.`;
      case 'placeholder': return `${fs.fileName}: Copied from template source`;
      case 'overridden':
        return `${fs.fileName}: Overridden — a placeholder replaces this file on Sync. `
          + `Reverts when a real IFC is re-uploaded.`;
      case 'missing': return `${fs.fileName}: Not yet synced`;
      default: return fs.fileName;
    }
  }

  // PORTAL-2203: set/clear the manual override flag on a real IFC file, then refresh the detail.
  // Marking only flags the file; the existing Sync button applies the overwrite (flag-then-sync).
  setFileOverride(fs: any, overridden: boolean): void {
    if (!fs?.homeIfcFileId || this.overridingFileId) return;

    const request = new UpdateHomeIfcFileRequest();
    request.id = fs.homeIfcFileId;
    request.companyId = this.selectedCompany?.id;
    request.isOverridden = overridden;

    this.overridingFileId = fs.homeIfcFileId;
    this.situViewApiClient.updateHomeIfcFile(request)
      .then(response => {
        if (response.success) {
          this.showMessage(overridden
            ? 'File marked to override — run Sync to apply'
            : 'Override removed');
          this.reloadExpandedDetail();
        } else {
          this.showMessage('Failed to update override');
        }
        this.overridingFileId = undefined;
      })
      .catch(() => {
        this.overridingFileId = undefined;
        this.showMessage('Could not update override');
      });
  }

  // Re-fetch the currently expanded template detail in place (without collapsing the row).
  private reloadExpandedDetail(): void {
    if (!this.expandedTemplateId) return;
    this.situViewApiClient.getPlaceholderTemplate(
      this.selectedCompany?.id, this.expandedTemplateId
    ).then((response: GetPlaceholderTemplateResponse) => {
      if (response.success && response.template) {
        this.expandedDetail = response.template;
        this.expandedFiles = this.getAllFilesForTemplate(response.template);
      }
    }).catch(() => { /* non-fatal: leave existing detail in place */ });
  }

  formatDate(date: Date | undefined): string {
    if (!date) return '\u2014';
    const d = new Date(date);
    return d.toLocaleDateString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, 'OK', { duration: 3000 });
  }
}
